import { CUSTOMER_SUB_TYPES, CUSTOMER_TYPES, INVOICE_CADENCES } from '@clockwise/shared';
import { z } from 'zod';

export type { CustomerSubType, CustomerType, InvoiceCadence } from '@clockwise/shared';
// Re-exported so customer-facing UI can pull classification enums, labels,
// and `{ value, label }` option lists from the same place it imports the
// `Customer` type, without reaching into @clockwise/shared directly.
export {
  CUSTOMER_SUB_TYPE_LABELS,
  CUSTOMER_SUB_TYPE_OPTIONS,
  CUSTOMER_SUB_TYPES,
  CUSTOMER_TYPE_LABELS,
  CUSTOMER_TYPE_OPTIONS,
  CUSTOMER_TYPES,
  INVOICE_CADENCE_LABELS,
  INVOICE_CADENCE_OPTIONS,
  INVOICE_CADENCES,
} from '@clockwise/shared';

const CustomFieldsRecord = z.record(z.string(), z.unknown());

export const CUSTOMER_STATUSES = ['active', 'inactive'] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const AddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});
export type Address = z.infer<typeof AddressSchema>;

export const CustomerSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  /** The business unit the customer belongs to. */
  businessUnitId: z.string().uuid(),
  qbId: z.string(),
  name: z.string(),
  legalName: z.string().nullable(),
  address: AddressSchema.nullable(),
  phone: z.string().nullable(),
  billingContactId: z.string().uuid().nullable(),
  billingAddress: AddressSchema.nullable(),
  status: z.enum(CUSTOMER_STATUSES),
  creditTerm: z.string().nullable(),
  startDate: z.string().nullable(),
  departmentId: z.string().uuid().nullable(),
  defaultInvoiceType: z.string().nullable(),
  corporateCalendar: z.string().nullable(),
  taxSpoc: z.string().nullable(),
  invoiceNotes: z.string().nullable(),
  invoiceCadence: z.enum(INVOICE_CADENCES).nullable(),
  customerType: z.enum(CUSTOMER_TYPES).nullable(),
  customerSubType: z.enum(CUSTOMER_SUB_TYPES).nullable(),
  salesRepId: z.string().uuid().nullable(),
  achAuthorized: z.boolean().nullable(),
  /** Parent company, for subsidiary customers. Null = stands alone. The
   *  hierarchy is one level deep — a parent never has a parent of its own. */
  parentCustomerId: z.string().uuid().nullable(),
  /** Invoicing pause, as a half-open window [from, until). `from` null = not
   *  paused; `until` null alongside a `from` = paused indefinitely. Use
   *  `isInvoicingPausedOn` from @clockwise/shared rather than reading the
   *  dates directly — "paused" depends on which date you are asking about. */
  invoicingPauseFrom: z.string().nullable(),
  invoicingPauseUntil: z.string().nullable(),
  /** Late payment fee. There is no `enabled` flag: a fee with no amount has
   *  nothing to charge, so "enabled" is derived from the amount being set.
   *  Use `hasLateFee` from @clockwise/shared rather than testing either half. */
  lateFeeKind: z.enum(['percentage', 'fixed_amount']).nullable(),
  /** numeric(12,2) — a string on the wire, never a JS number. */
  lateFeeAmount: z.string().nullable(),
  /** One document per period for everything this customer is billed, rather
   *  than one per engagement. An engagement can opt out on its own. */
  consolidatedInvoicing: z.boolean(),
  isInternal: z.boolean(),
  customFields: CustomFieldsRecord,
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Projected: display name of the in-effect Controller (customer team).
   *  Read-only; present on list responses, null when none is assigned. */
  controllerName: z.string().nullable().optional(),
  /** Projected: name of the parent company. Read-only; present on list
   *  responses and on the single-customer GET, null when the customer stands
   *  alone. Resolved server-side so the UI never has to fetch the parent
   *  through the view-scoped customer endpoint. */
  parentName: z.string().nullable().optional(),
  /** Projected: display name of the assigned Sales Rep, resolved server-side
   *  (a reference label — present on the single-customer GET, null when unset
   *  or the referenced employee no longer exists). The UI renders this instead
   *  of fetching the view-scoped GET /api/users/:id. */
  salesRepName: z.string().nullable().optional(),
});
export type Customer = z.infer<typeof CustomerSchema>;

/**
 * What the caller may do to ONE customer, computed by the API with the checks
 * its writes run (`GET /api/customers/:id` → `related.actions`). The detail
 * page gates its buttons on these, never on the role's permissions: a role's
 * scope, or the customer's Business Unit, can make an action the role holds
 * refuse on this customer.
 *
 * `customer.view` decides which customers a sub-record edit reaches; the
 * sub-record's own edit permission decides that it may be edited — so a role
 * can edit budgets without editing the customer.
 */
export interface CustomerActions {
  /** Edit the customer record itself (the header's Edit button). */
  edit: boolean;
  /** Admin-tier acts — amending a past version. */
  admin: boolean;
  /** Its customer-specific activities. */
  editActivities: boolean;
  editContacts: boolean;
  editTeam: boolean;
  /** Its engagements (SOWs). */
  editSows: boolean;
  editBudgets: boolean;
  editExpenses: boolean;
}

/** Nothing offered — what a screen assumes until the customer detail has
 *  loaded, so it never shows a button the answer may then take away. */
export const NO_CUSTOMER_ACTIONS: CustomerActions = {
  edit: false,
  admin: false,
  editActivities: false,
  editContacts: false,
  editTeam: false,
  editSows: false,
  editBudgets: false,
  editExpenses: false,
};

const CustomerBody = z.object({
  qbId: z.string(),
  name: z.string(),
  legalName: z.string().nullable().optional(),
  address: AddressSchema.nullable().optional(),
  phone: z.string().nullable().optional(),
  billingContactId: z.string().uuid().nullable().optional(),
  billingAddress: AddressSchema.nullable().optional(),
  creditTerm: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  defaultInvoiceType: z.string().nullable().optional(),
  corporateCalendar: z.string().nullable().optional(),
  taxSpoc: z.string().nullable().optional(),
  invoiceNotes: z.string().nullable().optional(),
  invoiceCadence: z.enum(INVOICE_CADENCES).nullable().optional(),
  customerType: z.enum(CUSTOMER_TYPES).nullable().optional(),
  customerSubType: z.enum(CUSTOMER_SUB_TYPES).nullable().optional(),
  salesRepId: z.string().uuid().nullable().optional(),
  achAuthorized: z.boolean().nullable().optional(),
  /** Both halves move together — the server refuses a kind with no amount and
   *  an amount with no kind. Sent as a NUMBER; the row stores numeric(12,2). */
  lateFeeKind: z.enum(['percentage', 'fixed_amount']).nullable().optional(),
  lateFeeAmount: z.number().nonnegative().nullable().optional(),
  consolidatedInvoicing: z.boolean().optional(),
  /** Null clears the link. The server rejects a parent that is inactive,
   *  internal, already a subsidiary, or the customer itself. */
  parentCustomerId: z.string().uuid().nullable().optional(),
  isInternal: z.boolean().optional(),
  customFields: CustomFieldsRecord.optional(),
});

export const CreateCustomerInput = CustomerBody.extend({
  /** The customer's unit; optional while the tenant has one. */
  businessUnitId: z.string().uuid().optional(),
});
export type CreateCustomerInput = z.infer<typeof CreateCustomerInput>;

export const PatchCustomerInput = CustomerBody.partial();
export type PatchCustomerInput = z.infer<typeof PatchCustomerInput>;

export interface ListCustomersQuery {
  q?: string;
  /** The customers of one business unit. */
  businessUnitId?: string;
  status?: CustomerStatus;
  qbId?: string;
  /** Subsidiaries of one parent — backs the customer detail's Subsidiaries
   *  tab without a dedicated route. */
  parentCustomerId?: string;
  /** Optional single custom-field filter — pair (id + value). Must be passed
   *  together; the server ignores the pair if either is missing. */
  customFieldId?: string;
  customFieldValue?: string;
  sortField?:
    | 'name'
    | 'qbId'
    | 'parentName'
    | 'controllerName'
    | 'status'
    | 'createdAt'
    | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Customer export filter. Mirrors the list-page filters (status, custom
 * fields, search text, sort) so the downloaded file matches what the user
 * sees on screen. Subset of `ListCustomersQuery` minus pagination — the
 * export drains the whole filtered result. Also reused by the contacts and
 * team exports, which scope their rows to the customers matching this filter.
 */
export type ExportCustomersQuery = Pick<
  ListCustomersQuery,
  | 'q'
  | 'status'
  | 'qbId'
  | 'parentCustomerId'
  | 'customFieldId'
  | 'customFieldValue'
  | 'sortField'
  | 'sortDirection'
>;

export interface PauseInvoicingInput {
  /** First day of the pause. Omitted = today, so a bodyless call still means
   *  "pause them now"; supplying it schedules the pause. */
  from?: string;
  /** First day invoicing resumes, exclusive. Omitted/null = indefinite. */
  until?: string | null;
}

/** One row of the customers import report, in upload order. */
export type ImportRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportCustomersRow {
  rowNumber: number;
  qbId: string | null;
  outcome: ImportRowOutcome;
  errors?: string[];
}

export interface ImportCustomersResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportCustomersRow[];
}

/** `GET /api/customers/:id/subsidiaries` — the customers whose parent is this
 *  one. A nested collection rather than a field on the customer, so the API
 *  shape does not track whichever screen renders it. */
export interface ListSubsidiariesQuery {
  sortField?: 'name' | 'qbId' | 'status' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}
